/**
 * Push Notification CrossFrame Listeners
 *
 * Handles CrossFrame requests for push device registration/deregistration.
 * Delegates to the Gateway client via getServiceClient() -- same pattern
 * as getNotificationsListener, fixtureListeners, etc.
 *
 * Message flow:
 *   RN Shell -> injectJavaScript(postMessage) -> CrossFrameUtils -> this listener
 *   -> Coral Client (AuthCoralHandler auto-attaches Bearer token) -> Gateway
 *   -> ACK posted back to RN Shell via window.ReactNativeWebView.postMessage()
 *   -> RN clears pending retry timers on success
 */

import { getServiceClient } from '@api/client-factory/StoreManagementClientFactory'
import { RegisterPushDeviceInput } from '@amzn/coral_com-amazon-jwostoremanagementgateway/RegisterPushDeviceInput'
import { RegisterPushDeviceOutput } from '@amzn/coral_com-amazon-jwostoremanagementgateway/RegisterPushDeviceOutput'
import { DeregisterPushDeviceInput } from '@amzn/coral_com-amazon-jwostoremanagementgateway/DeregisterPushDeviceInput'
import { DeregisterPushDeviceOutput } from '@amzn/coral_com-amazon-jwostoremanagementgateway/DeregisterPushDeviceOutput'
import { validateRequired } from './listenerUtils'

const TAG = 'SMP: [PushNotification]'

/**
 * Send acknowledgement back to the RN shell via ReactNativeWebView.postMessage.
 * The RN shell listens for this in its onMessage handler and clears retry timers.
 */
function sendAckToNative (operation: string, success: boolean, data?: Record<string, any>): void {
    try {
        if ((window as any).ReactNativeWebView) {
            (window as any).ReactNativeWebView.postMessage(JSON.stringify({
                type: 'PUSH_NOTIFICATION_ACK',
                operation,
                success,
                ...data,
            }))
            console.log(TAG, `Sent ${operation} ACK to RN shell: success=${success}`)
        }
    } catch (e) {
        console.warn(TAG, `Failed to send ${operation} ACK to RN shell:`, e)
    }
}

/**
 * Handles registerPushDevice CrossFrame request from native shell.
 *
 * Expected message.data shape:
 * {
 *   deviceToken: string;      // FCM/APNs token
 *   deviceId: string;         // e.g. 'ffdb9103348fb2f' (ANDROID_ID) or '07BDE22E-...' (IDFV)
 *   devicePlatform: string;   // 'ANDROID' | 'IOS'
 *   appAuthMode: string;      // e.g. 'BIOMETRIC_AUTH'
 *   appVersion?: string;      // Application version string (optional)
 * }
 */
export async function registerPushDeviceListener (message: any): Promise<RegisterPushDeviceOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'deviceToken', 'devicePlatform')

    console.log(TAG, 'Registering push device via Gateway client:', {
        deviceId: params.deviceId,
        platform: params.devicePlatform,
    })

    const client = getServiceClient()
    const input = RegisterPushDeviceInput.clone({
        deviceToken: params.deviceToken,
        deviceId: params.deviceId,
        devicePlatform: params.devicePlatform,
        appVersion: params.appVersion,
        appAuthMode: params.appAuthMode,
    })

    try {
        const result = await client.registerPushDevice(input).toPromise()
        console.log(TAG, 'RegisterPushDevice success:', { expiresAt: result?.expiresAt })
        sendAckToNative('registerPushDevice', true, { expiresAt: result?.expiresAt })
        return result
    } catch (error: any) {
        console.error(TAG, 'RegisterPushDevice failed:', error?.message)
        sendAckToNative('registerPushDevice', false, { error: error?.message })
        throw error
    }
}

/**
 * Handles deregisterPushDevice CrossFrame request from native shell.
 *
 * Expected message.data shape:
 * {
 *   deviceToken: string;       // FCM/APNs token to deregister
 *   devicePlatform?: string;   // Optional platform hint
 * }
 */
export async function deregisterPushDeviceListener (message: any): Promise<DeregisterPushDeviceOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'deviceToken')

    console.log(TAG, 'Deregistering push device via Gateway client:', { platform: params.devicePlatform })

    const client = getServiceClient()
    const input = DeregisterPushDeviceInput.clone({
        deviceToken: params.deviceToken,
        devicePlatform: params.devicePlatform,
    })

    try {
        const result = await client.deregisterPushDevice(input).toPromise()
        console.log(TAG, 'DeregisterPushDevice success:', { status: result?.status })
        sendAckToNative('deregisterPushDevice', true)
        return result
    } catch (error: any) {
        console.error(TAG, 'DeregisterPushDevice failed:', error?.message)
        sendAckToNative('deregisterPushDevice', false, { error: error?.message })
        throw error
    }
}
